import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useLang } from "@/context/LanguageContext";
import { CalendarIcon, Loader2 } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { format } from "date-fns";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BookingForm() {
  const { t, lang } = useLang();
  const [packages, setPackages] = useState([]);
  const [form, setForm] = useState({
    package_id: "classic_rivas",
    full_name: "",
    email: "",
    phone: "",
    passengers: 1,
    preferred_date: "",
    payment_type: "deposit",
    notes: "",
  });
  const [date, setDate] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    axios.get(`${API}/packages`).then(r => setPackages(r.data)).catch(() => {});
    const handler = (e) => setForm(f => ({ ...f, package_id: e.detail }));
    window.addEventListener("prefill-package", handler);
    return () => window.removeEventListener("prefill-package", handler);
  }, []);

  const selectedPkg = packages.find(p => p.id === form.package_id);
  const total = selectedPkg
    ? (form.payment_type === "deposit" ? selectedPkg.deposit : selectedPkg.full_price) * form.passengers
    : 0;

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.email || !form.phone || !date) {
      toast.error(t.booking.error);
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/bookings/checkout`, {
        ...form,
        preferred_date: format(date, "yyyy-MM-dd"),
        origin_url: window.location.origin,
        language: lang,
      });
      toast.success(t.booking.success);
      window.location.href = res.data.url;
    } catch (err) {
      toast.error(t.booking.error);
    } finally {
      setLoading(false);
    }
  };

  const inputCls = "w-full bg-transparent border border-jungle/25 rounded-sm px-4 py-3 text-jungle text-sm focus:border-terracotta focus:outline-none transition-colors";

  return (
    <section id="book" data-testid="booking-section" className="py-24 md:py-32 bg-sand">
      <div className="max-w-7xl mx-auto px-6 md:px-12 grid md:grid-cols-2 gap-12 md:gap-16 items-start">
        <div className="md:sticky md:top-32">
          <div className="font-overline text-terracotta text-xs mb-3">{t.booking.overline}</div>
          <h2 className="font-display text-4xl md:text-5xl text-jungle leading-[1.05]">{t.booking.title}</h2>
          <p className="text-forest mt-5 max-w-md text-base leading-relaxed">{t.booking.subtitle}</p>
          <div className="mt-8 relative overflow-hidden aspect-[4/5] border border-jungle/15 hide-md">
            <img src="https://ik.imagekit.io/paraglidetour/parapente-perez-zeledon-tandem-costa-rica-puravda-010.jpeg" alt="Paragliding aerial" className="w-full h-full object-cover" />
          </div>
        </div>

        <form data-testid="booking-form" onSubmit={submit} className="bg-white border border-jungle/15 p-6 md:p-10 space-y-5">
          <div>
            <label className="font-overline text-[10px] text-forest mb-2 block">{t.booking.package}</label>
            <select
              data-testid="booking-package-select"
              className={inputCls}
              value={form.package_id}
              onChange={(e) => setForm({ ...form, package_id: e.target.value })}
            >
              <option value="">Select</option>
              {packages.map(p => (
                <option key={p.id} value={p.id}>{`${p.name} — $${p.full_price}`}</option>
              ))}
              
            </select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="font-overline text-[10px] text-forest mb-2 block">{t.booking.date}</label>
              <Popover>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    data-testid="booking-date-btn"
                    className={inputCls + " flex items-center justify-between text-left"}
                  >
                    {date ? format(date, "PPP") : <span className="text-forest/60">—</span>}
                    <CalendarIcon size={16} className="text-forest" />
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar mode="single" selected={date} onSelect={setDate} disabled={(d) => d < new Date(new Date().setHours(0,0,0,0))} />
                </PopoverContent>
              </Popover>
            </div>
            <div>
              <label className="font-overline text-[10px] text-forest mb-2 block">{t.booking.passengers}</label>
              <input
                data-testid="booking-passengers-input"
                type="number"
                min={1}
                max={6}
                value={form.passengers}
                onChange={(e) => setForm({ ...form, passengers: parseInt(e.target.value) || 1 })}
                className={inputCls}
              />
            </div>
          </div>

          <div>
            <label className="font-overline text-[10px] text-forest mb-2 block">{t.booking.name}</label>
            <input
              data-testid="booking-name-input"
              type="text"
              value={form.full_name}
              onChange={(e) => setForm({ ...form, full_name: e.target.value })}
              className={inputCls}
              required
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="font-overline text-[10px] text-forest mb-2 block">{t.booking.email}</label>
              <input
                data-testid="booking-email-input"
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className={inputCls}
                required
              />
            </div>
            <div>
              <label className="font-overline text-[10px] text-forest mb-2 block">{t.booking.phone}</label>
              <input
                data-testid="booking-phone-input"
                type="tel"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                className={inputCls}
                required
              />
            </div>
          </div>

          <div>
            <label className="font-overline text-[10px] text-forest mb-2 block">{t.booking.notes}</label>
            <textarea
              data-testid="booking-notes-input"
              rows={2}
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              className={inputCls + " resize-none"}
            />
          </div>

          <div>
            <label className="font-overline text-[10px] text-forest mb-2 block">{t.booking.payment_type}</label>
            <div className="grid grid-cols-2 gap-3">
              {["deposit", "full"].map(type => (
                <button
                  key={type}
                  type="button"
                  data-testid={`booking-payment-${type}`}
                  onClick={() => setForm({ ...form, payment_type: type })}
                  className={`border rounded-sm px-4 py-3 text-sm transition-colors ${
                    form.payment_type === type
                      ? "border-terracotta bg-terracotta/10 text-jungle"
                      : "border-jungle/20 text-forest hover:border-jungle/40"
                  }`}
                >
                  {type === "deposit" ? t.booking.deposit : t.booking.full}
                </button>
              ))}
            </div>
          </div>

          <div className="pt-4 border-t border-jungle/10 flex items-center justify-between">
            <div>
              <div className="font-overline text-[10px] text-forest">Total {t.booking.payment_type}</div>
              <div className="font-display text-3xl text-jungle leading-none">${total.toFixed(2)}</div>
            </div>
            <button
              type="submit"
              data-testid="booking-submit-btn"
              disabled={loading}
              className="btn-primary disabled:opacity-70"
            >
              {loading ? <><Loader2 size={16} className="animate-spin" /> {t.booking.submitting}</> : t.booking.submit}
            </button>
          </div>
        </form>
      </div>
    </section>
  );
}
